export default function DentalBookPrivacyPolicy() {
  return (
    <>
      <div>
        <p>
          <strong>Effective Date:</strong> September 9, 2026
        </p>
        <p>
          <strong>Last Updated:</strong> September 9, 2026
        </p>
        <p>
          Agape MedTecs Inc. (&quot;Agape MedTecs,&quot; &quot;AIDentalBook,&quot; &quot;we,&quot;
          &quot;us,&quot; or &quot;our&quot;) respects your privacy and is committed to protecting your
          personal and dental health information.
        </p>
        <p>
          This Privacy Policy applies to the AIDentalBook website, iDentalBook patient application, mobile
          applications, and related services (collectively, the &quot;Services&quot;).
        </p>

        <hr />

        <section>
          <h4>1. Information We Collect</h4>
          <p>Depending on how you use our Services, we may collect:</p>
          <ul style={{listStyleType: 'disc', marginLeft: '20px'}}>
            <li>Name, contact information, and account information</li>
            <li>Dental and health information provided by you or your dental providers</li>
            <li>Dental X-rays, photographs, CBCT images, intraoral scans, and digital dental 3D models</li>
            <li>Treatment, appointment, insurance, payment, and dental history information</li>
            <li>Information you upload, submit, or store in your account</li>
            <li>Device, IP address, login, security, and technical information</li>
            <li>Information necessary to provide customer support and operate our Services</li>
          </ul>
          <p>
            We collect information directly from you, from participating dental providers, and through
            authorized service providers.
          </p>
        </section>

        <hr />

        <section>
          <h4>2. Protected Health Information (PHI)</h4>
          <p>
            When AIDentalBook provides services involving protected health information (&quot;PHI&quot;) on
            behalf of a HIPAA-covered dental provider, we handle PHI in accordance with applicable HIPAA
            requirements and our agreements with the provider.
          </p>
          <p>We use and disclose PHI only as permitted or required by law, including for:</p>
          <ul style={{listStyleType: 'disc', marginLeft: '20px'}}>
            <li>Treatment and dental care</li>
            <li>Payment and insurance activities</li>
            <li>Health care operations</li>
            <li>Appointment and service communications</li>
            <li>Providing requested services</li>
            <li>Legal and regulatory requirements</li>
            <li>Other purposes authorized by applicable law</li>
          </ul>
          <p>
            <strong>We do not sell PHI.</strong>
          </p>
        </section>

        <hr />

        <section>
          <h4>3. How We Use Information</h4>
          <p>We may use information to:</p>
          <ul style={{listStyleType: 'disc', marginLeft: '20px'}}>
            <li>Provide, maintain, and improve the Services</li>
            <li>Store and manage dental records</li>
            <li>Facilitate communication between patients and dental professionals</li>
            <li>Provide AI-assisted tools and analysis where offered</li>
            <li>Process appointments, payments, insurance, and account services</li>
            <li>Provide customer support</li>
            <li>Protect the security of our Services</li>
            <li>Detect fraud, abuse, and unauthorized activity</li>
            <li>Comply with legal and regulatory requirements</li>
          </ul>
          <p>
            Where information is used for product development, analytics, research, or improvement, we will
            use de-identified or aggregated information whenever reasonably possible.
          </p>
        </section>

        <hr />

        <section>
          <h4>4. AI and Automated Services</h4>
          <p>
            AIDentalBook may provide AI-assisted dental information, analysis, organization, or other
            automated features.
          </p>
          <p>
            AI-generated information is intended to{' '}
            <strong>support—not replace—the judgment of qualified dental or medical professionals</strong>.
            Patients should consult an appropriate health care professional before making treatment decisions.
          </p>
          <p>
            Where PHI is used by an AI or technology service provider on behalf of a covered entity,
            appropriate contractual, security, and HIPAA requirements will apply.
          </p>
        </section>

        <hr />

        <section>
          <h4>5. Service Providers and Business Associates</h4>
          <p>We may use trusted third-party companies to provide services such as:</p>
          <ul style={{listStyleType: 'disc', marginLeft: '20px'}}>
            <li>Cloud hosting and data storage</li>
            <li>Security and authentication</li>
            <li>Communications</li>
            <li>Payment processing</li>
            <li>Technical support</li>
            <li>Analytics and system operations</li>
          </ul>
          <p>
            When required by HIPAA, we enter into appropriate{' '}
            <strong>Business Associate Agreements (BAAs)</strong> and require applicable safeguards for PHI.
          </p>
        </section>

        <hr />

        <section>
          <h4>6. Cookies and Online Tracking</h4>
          <p>We may use cookies and similar technologies to operate, secure, and improve our Services.</p>
          <p>
            AIDentalBook does{' '}
            <strong>not knowingly use PHI for targeted advertising or behavioral advertising</strong>, and we
            do not permit advertising networks to receive PHI for such purposes.
          </p>
          <p>
            Tracking technologies used within authenticated patient areas will be limited to purposes such as
            security, authentication, fraud prevention, system operation, and permitted analytics.
          </p>
          <p>
            Any use of tracking technologies involving PHI will be reviewed for applicable HIPAA requirements.
          </p>
        </section>

        <hr />

        <section>
          <h4>7. Marketing and Advertising</h4>
          <p>AIDentalBook does not sell your PHI.</p>
          <p>
            We do not use PHI for targeted advertising without the authorization or other legal basis required
            by applicable law.
          </p>
          <p>
            Where HIPAA requires a written authorization for marketing or another use or disclosure of PHI, we
            will obtain that authorization.
          </p>
        </section>

        <hr />

        <section>
          <h4>8. Sharing Information</h4>
          <p>We may disclose information:</p>
          <ul style={{listStyleType: 'disc', marginLeft: '20px'}}>
            <li>To your dental or health care providers at your direction or as permitted by law</li>
            <li>To service providers necessary to operate the Services</li>
            <li>When required by law, court order, or governmental authority</li>
            <li>To protect the rights, safety, and security of AIDentalBook, users, or others</li>
            <li>
              In connection with a merger, acquisition, financing, or sale of assets, subject to applicable
              privacy requirements
            </li>
          </ul>
          <p>We will limit PHI disclosures as required by applicable law.</p>
        </section>

        <hr />

        <section>
          <h4>9. Your Privacy Rights</h4>
          <p>Depending on applicable law, you may have rights to:</p>
          <ul style={{listStyleType: 'disc', marginLeft: '20px'}}>
            <li>Access and obtain a copy of your health information</li>
            <li>Request correction of your information</li>
            <li>Request restrictions on certain uses or disclosures</li>
            <li>Request confidential communications</li>
            <li>Request an accounting of certain disclosures</li>
            <li>Receive a copy of applicable privacy notices</li>
            <li>Withdraw certain permissions where legally permitted</li>
          </ul>
          <p>To exercise applicable rights, contact us using the information below.</p>
        </section>

        <hr />

        <section>
          <h4>10. Security</h4>
          <p>
            We maintain administrative, technical, and physical safeguards designed to protect information
            against unauthorized access, use, disclosure, alteration, or destruction.
          </p>
          <p>Security measures may include:</p>
          <ul style={{listStyleType: 'disc', marginLeft: '20px'}}>
            <li>Access controls and authentication</li>
            <li>Encryption where appropriate</li>
            <li>Audit logging and monitoring</li>
            <li>Backup and disaster recovery</li>
            <li>Security risk assessments</li>
            <li>Workforce security and training</li>
            <li>Incident response procedures</li>
          </ul>
          <p>No electronic system can guarantee absolute security.</p>
        </section>

        <hr />

        <section>
          <h4>11. Data Retention</h4>
          <p>
            We retain information for as long as reasonably necessary to provide the Services, meet
            contractual and legal obligations, maintain appropriate records, resolve disputes, and protect our
            legitimate interests.
          </p>
          <p>
            Health records may be retained according to applicable health care record-retention requirements.
          </p>
        </section>

        <hr />

        <section>
          <h4>12. Children and Minors</h4>
          <p>
            The Services may be used by minors when permitted by applicable law and when appropriately
            authorized or managed by a parent, guardian, or authorized representative.
          </p>
          <p>Privacy rights relating to minors may vary depending on applicable federal and state law.</p>
        </section>

        <hr />

        <section>
          <h4>13. California Privacy Rights</h4>
          <p>
            California residents may have additional rights under applicable California privacy laws,
            including rights relating to access, correction, deletion, and information about the collection
            and use of personal information.
          </p>
          <p>
            Certain health information may be subject to exemptions or separate requirements under HIPAA and
            other health privacy laws.
          </p>
        </section>

        <hr />

        <section>
          <h4>14. Third-Party Websites and Services</h4>
          <p>
            Our Services may contain links or integrations to third-party websites, applications, or services.
          </p>
          <p>
            Those third parties may have their own privacy policies and practices. AIDentalBook is not
            responsible for the privacy practices of third-party services that we do not control.
          </p>
        </section>

        <hr />

        <section>
          <h4>15. International Users</h4>
          <p>
            Information may be processed or stored in the United States or other locations where AIDentalBook
            or its service providers operate.
          </p>
          <p>
            By using the Services, you understand that information may be processed in accordance with
            applicable law and this Privacy Policy.
          </p>
        </section>

        <hr />

        <section>
          <h4>16. HIPAA Notice of Privacy Practices</h4>
          <p>
            If AIDentalBook provides Services as a Business Associate to a HIPAA-covered dental provider, the
            provider&apos;s <strong>Notice of Privacy Practices</strong> may contain additional information
            concerning how that provider uses and discloses your PHI.
          </p>
          <p>
            This Privacy Policy does not replace a covered provider&apos;s legally required HIPAA Notice of
            Privacy Practices.
          </p>
        </section>

        <hr />

        <section>
          <h4>17. Changes to This Policy</h4>
          <p>We may update this Privacy Policy from time to time.</p>
          <p>
            When we make material changes, we will post the updated policy and revise the &quot;Last
            Updated&quot; date. Additional notice will be provided when required by law.
          </p>
        </section>

        <hr />

        <section>
          <h4>18. Contact Us</h4>
          <p>For privacy questions, requests, or concerns:</p>
          <p>
            <strong>Agape MedTecs Inc.</strong>
            <br />
            10440 S. De Anza Blvd., Suite D4
            <br />
            Cupertino, CA 95014
            <br />
            <strong>Email:</strong> <a href="mailto:privacy@aidentalbook.com">privacy@aidentalbook.com</a>
          </p>
          <p>
            You may also have the right to submit a complaint to the U.S. Department of Health and Human
            Services, Office for Civil Rights, if you believe your HIPAA privacy rights have been violated. We
            will not retaliate against you for filing a privacy complaint.
          </p>
        </section>

        <hr />

        <section>
          <h4>Important Compliance Statement</h4>
          <p>
            This Privacy Policy describes AIDentalBook&apos;s intended privacy practices.{' '}
            <strong>A Privacy Policy alone does not establish HIPAA compliance.</strong>
          </p>
          <p>
            Actual compliance depends on AIDentalBook&apos;s technical, administrative, and physical
            safeguards, vendor agreements, Business Associate Agreements, access controls, security risk
            management, incident response procedures, and actual data practices.
          </p>
          <p>This policy should be reviewed by qualified privacy/HIPAA counsel before implementation.</p>
          <p>
            AIDentalBook will periodically review its privacy and security practices to ensure that this
            Privacy Policy accurately describes its actual practices.
          </p>
          <p>&copy; 2026 Agape MedTecs Inc. All rights reserved.</p>
        </section>
      </div>
    </>
  );
}
